import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useAnimationConfig } from '../../../../context/occ/hooks/use-animation-config';
import { getKeyCodeForEvent, KeyCode } from '../../../../utils/key-code';
import { DropdownButton } from './subcomponents/button';
import { DropdownContent } from './subcomponents/content';
import { DropdownContextInstance, mapDropdownContext } from './types/context';
import { DropdownMode } from './types/mode';
const b = bemClassBuilder('v-dropdown');
/**
 * Renders a dropdown component.
 * Note that dropdown is a managed component, meaning that it does not store the value internally.
 * You must provide a `value` and `onChange` handler if you want the dropdown to work like a native `<select>`.
 */
export const Dropdown = observer(props => {
    var _a;
    const { value, options, className, onChangeValue, children, modal } = props;
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [searchTerm, setSearchTerm] = useState('');
    const { enableAnimations } = useAnimationConfig();
    const defaultComponentId = useId();
    const selectedOptionIndex = filteredOptions
        ? filteredOptions.findIndex(o => o.value === value)
        : options.findIndex(o => o.value === value);
    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);
    const setDropdownExpanded = useCallback((expanded) => {
        setIsExpanded(expanded);
        if (!expanded) {
            setSearchTerm('');
            setFilteredOptions(options);
        }
    }, [options]);
    useEffect(() => {
        if (!isExpanded) {
            return;
        }
        const handleDocumentClicked = (event) => {
            const container = containerRef.current;
            const target = event.target;
            if (!container || !target) {
                return;
            }
            if (!container.contains(target)) {
                setDropdownExpanded(false);
            }
        };
        document.addEventListener('click', handleDocumentClicked, false);
        return () => document.removeEventListener('click', handleDocumentClicked, false);
    }, [isExpanded, options, setDropdownExpanded]);
    useEffect(() => {
        if (value && listRef.current) {
            listRef.current.scrollOptionIntoView(value);
        }
    }, [value]);
    const handleKeyDown = (event) => {
        const keyCode = getKeyCodeForEvent(event);
        if (keyCode == null) {
            return;
        }
        if (isNavigationKeyCode(keyCode) || keyCode === KeyCode.Enter) {
            event.preventDefault();
        }
        switch (keyCode) {
            case KeyCode.UpArrow:
                return navigateUp();
            case KeyCode.DownArrow:
                return navigateDown();
            case KeyCode.PageUp:
                if (!listRef.current) {
                    return;
                }
                return listRef.current.scrollPageUp();
            case KeyCode.PageDown:
                if (!listRef.current) {
                    return;
                }
                return listRef.current.scrollPageDown();
            case KeyCode.Escape:
            case KeyCode.Tab:
            case KeyCode.Enter:
                if (!isExpanded) {
                    return;
                }
                return setDropdownExpanded(false);
            default:
                return;
        }
    };
    const handleOptionChanged = useCallback((option) => {
        if (value === option.value) {
            return;
        }
        onChangeValue === null || onChangeValue === void 0 ? void 0 : onChangeValue(option.value);
    }, [onChangeValue, value]);
    const handleOptionClicked = useCallback((option) => {
        handleOptionChanged(option);
        setDropdownExpanded(false);
    }, [handleOptionChanged, setDropdownExpanded]);
    const handleSearchResultsChanged = useCallback((results) => {
        setFilteredOptions(results !== null && results !== void 0 ? results : options);
    }, [options, setFilteredOptions]);
    const toggleDropdown = useCallback(() => {
        setDropdownExpanded(!isExpanded);
    }, [isExpanded, setDropdownExpanded]);
    const context = useMemo(() => {
        return mapDropdownContext(props, { containerRef, isDropdownExpanded: isExpanded, filteredOptions, searchTerm }, {
            onOptionClicked: handleOptionClicked,
            onSearchTermChanged: setSearchTerm,
            onSearchResultsChanged: handleSearchResultsChanged,
            toggleDropdown
        }, defaultComponentId);
    }, [
        filteredOptions,
        handleOptionClicked,
        handleSearchResultsChanged,
        isExpanded,
        props,
        searchTerm,
        toggleDropdown,
        defaultComponentId
    ]);
    const handleDropdownButtonClicked = () => {
        if (!context.showSearch || !isExpanded) {
            toggleDropdown();
        }
    };
    const navigateUp = () => {
        const previousOptionIndex = selectedOptionIndex < 1 ? 0 : selectedOptionIndex - 1;
        let availableOptionIndex = undefined;
        for (let index = previousOptionIndex; index >= 0; index--) {
            if (!filteredOptions[index].disabled) {
                availableOptionIndex = index;
                break;
            }
        }
        if (availableOptionIndex === undefined) {
            return;
        }
        handleOptionChanged(filteredOptions[availableOptionIndex]);
    };
    const navigateDown = () => {
        const maxIndex = filteredOptions.length - 1;
        if (selectedOptionIndex === maxIndex) {
            return;
        }
        const nextOptionIndex = selectedOptionIndex + 1;
        let availableOptionIndex = undefined;
        for (let index = nextOptionIndex; index < filteredOptions.length; index++) {
            if (!filteredOptions[index].disabled) {
                availableOptionIndex = index;
                break;
            }
        }
        if (!availableOptionIndex) {
            return;
        }
        handleOptionChanged(filteredOptions[availableOptionIndex]);
    };
    if (options.length === 0 && !props.showEmptyDropdown) {
        return null;
    }
    const dropdown = (_jsx(DropdownContextInstance.Provider, { value: context, children: _jsxs("div", { id: context.dropdownId, className: b.block({
                isExpanded,
                hasValue: !!context.selectedOption,
                focused: isExpanded,
                mode: (_a = props.mode) !== null && _a !== void 0 ? _a : DropdownMode.Normal
            }, className), ref: containerRef, onKeyDown: handleKeyDown, children: [children, _jsx(DropdownButton, Object.assign({}, props, { isExpanded: isExpanded, iconName: props.buttonIconName, loadingIconName: props.buttonLoadingIconName, onClick: handleDropdownButtonClicked, hasLabelElement: !!children, onIconClick: toggleDropdown })), _jsx(DropdownContent, Object.assign({ ref: listRef }, modal))] }) }));
    return enableAnimations ? _jsx(AnimatePresence, { children: dropdown }) : dropdown;
});
Dropdown.displayName = 'Dropdown';
const isNavigationKeyCode = (keyCode) => {
    return (keyCode === KeyCode.UpArrow ||
        keyCode === KeyCode.DownArrow ||
        keyCode === KeyCode.PageUp ||
        keyCode === KeyCode.PageDown);
};
//# sourceMappingURL=dropdown.js.map